import * as React from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";
import { Text, MonoText } from "./typography";
import { CurrencyValue } from "./value-display";
import { API_BASE_URL } from '../../lib/api';

/**
 * DeleteWithdrawalDialog - Confirmation dialog for deleting a withdrawal
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when withdrawal is deleted successfully
 * @param {Object} props.withdrawal - The withdrawal to delete
 */
export function DeleteWithdrawalDialog({
  open,
  onOpenChange,
  onSuccess,
  withdrawal,
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Reset error when dialog opens
  React.useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!withdrawal?.transactionId) {
      setError("No withdrawal selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete(
        `${API_BASE_URL}/transactions/withdrawals/${withdrawal.transactionId}`,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to delete withdrawal:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!withdrawal) {
    return null;
  }

  const REASON_LABELS = {
    living_expenses: "Living Expenses",
    emergency: "Emergency Fund",
    major_purchase: "Major Purchase",
    rebalancing: "Portfolio Rebalancing",
    tax_payment: "Tax Payment",
    other: "Other",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Withdrawal
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete this
            withdrawal record.
          </DialogDescription>
        </DialogHeader>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive p-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}

        {/* Withdrawal Details */}
        <div className="rounded-lg border border-border/50 p-4 space-y-2">
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Amount</Text>
            <CurrencyValue
              value={withdrawal.amount}
              currency={withdrawal.currency}
              size="sm"
              className="font-medium"
            />
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Date</Text>
            <MonoText size="sm">{withdrawal.date || withdrawal.withdrawalDate}</MonoText>
          </div>
          {withdrawal.reason && (
            <div className="flex justify-between items-center">
              <Text variant="secondary" size="sm">Reason</Text>
              <Text size="sm" className="font-medium">
                {REASON_LABELS[withdrawal.reason] || withdrawal.reason}
              </Text>
            </div>
          )}
          {withdrawal.notes && (
            <div className="flex justify-between items-start">
              <Text variant="secondary" size="sm">Notes</Text>
              <Text size="sm" className="text-right max-w-[200px]">
                {withdrawal.notes}
              </Text>
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteWithdrawalDialog;
